/**
 * The control registry — the one place a control's appearance is decided.
 *
 * Requirement, in the operator's words: change the filter bar once and every filter and
 * search control on the site changes with it. That only holds if there is exactly one
 * place to change, so this file is it. Nothing under `apps/web/src` outside `ui/` may
 * write a control class string; `scripts/check-ui-primitives.mjs` fails the build on it.
 *
 * Two halves make the guarantee, and neither works alone:
 *
 *  1. THIS FILE owns the class names. A primitive reads a frozen record; it never
 *     concatenates a caller's string into the result.
 *  2. `styles/global.css` drives every control's colour from the `--ctl-*` custom
 *     properties declared beside the theme tokens. Renaming a class here would not have
 *     turned anything purple on its own — the paint has to be single-sourced too.
 *
 * Deliberately not cva or tailwind-variants: a frozen `Record` is the whole mechanism,
 * it adds no dependency, and it is greppable. Vigilis reached for cva in four of its
 * twenty-six primitives and hand-rolled the other twenty-two, which is how it ended up
 * with two vocabularies and no propagation at all.
 */

/** Buttons that perform an action. `primary` is the bare `.btn` look. */
export const BUTTON_TONE = Object.freeze({
  primary: '',
  ok: 'btn-ok',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
  quiet: 'btn-quiet',
});

export type ButtonTone = keyof typeof BUTTON_TONE;

/**
 * Two heights, not six. The stylesheet grew `.btn` at 44px, `.btn-sm` at 36, `.toggle` at
 * 38, `.pager button` at 34 and `.fbtn` at 32 — five targets for what a reader experiences
 * as one kind of control. `md` is the 44px WCAG 2.2 target size and the default; `sm` is
 * for dense rows where the control repeats per line.
 */
export const BUTTON_SIZE = Object.freeze({
  md: '',
  sm: 'btn-sm',
});

export type ButtonSize = keyof typeof BUTTON_SIZE;

export const BUTTON_BASE = 'btn';

/**
 * Chrome controls — filters, view switches, pagination, and the inputs that sit beside
 * them. These share one paint vocabulary (`--ctl-*`) because to a reader they are one
 * family: things that narrow or move what is on screen, as opposed to things that enter
 * data or commit an action.
 */
export const CHROME = Object.freeze({
  /** A pill that toggles a filter on or off. Carries `aria-pressed`. */
  filter: 'fbtn',
  /** A view switch — same family as a filter, rectangular because it is exclusive. */
  toggle: 'toggle',
  /**
   * The check drawn in a pressed filter or toggle. "Pressed" has to be a shape as well as a
   * colour (WCAG 1.4.1): the hue change alone was 1.18:1 for the text.
   */
  pressedMark: 'ctl-pressed-mark',
  /** Pagination. Was `.pager button`, a position-keyed rule; now a class of its own. */
  pager: 'ctl-pager',
  /** Text inputs, selects and textareas. */
  field: 'ctl',
  /**
   * A field holding two or three characters — an hour, a number of days. Without it the input
   * takes the row's full width, so a box for "18" is as wide as one for an email address and
   * reads as though it wants a sentence.
   */
  short: 'ctl-short',
  /**
   * A date box. Its own width rather than `short`'s: a date is ten characters plus whatever
   * calendar affordance the browser draws inside the field, and `short` is sized for "18".
   * Bare `.ctl` is width:100%, which makes a date box as wide as an email field.
   */
  date: 'ctl-date',
  /** A field whose value could not be read. Pairs with `aria-invalid`; never colour alone. */
  invalid: 'ctl-invalid',
});

/**
 * A checkbox and the sentence it toggles. Its own two names rather than `CHROME.field`,
 * because a box is not a text control: it takes none of the field's width, padding or
 * height, and giving it that class made it a 44px-tall bordered slab with a tick in it.
 */
export const CHECKBOX = Object.freeze({
  box: 'ctl-check',
  /** Box and label on one line, sized as a single target. */
  row: 'ctl-check-row',
});

/**
 * A radio rendered as a card: the option's name, the consequence of picking it underneath,
 * and the whole card as the target. Its own names rather than `CHROME`'s because a choice is
 * not chrome — chrome narrows what is on screen, a radio commits the reader to one branch.
 *
 * The box itself is `CHECKBOX.box`, not a name of its own. A tick and a dot are the same
 * 18px target with the same accent, and giving them separate names is how `.fbtn` and
 * `.toggle` drifted to different heights.
 */
export const RADIO = Object.freeze({
  card: 'opt',
  label: 'opt-l',
  description: 'opt-d',
  group: 'radios',
});

/**
 * A group of related fields under one legend — a `fieldset`, which is the element that already
 * means this and the only one a screen reader announces the legend with.
 *
 * Its own names rather than reusing `Field`'s, because a group is not a field: it has no
 * control, no label association, and its `legend` is read once on entry rather than on every
 * focus. `summary` is for a derived sentence about the group as a whole — "Nudge 3 fires first,
 * on day 3" — which belongs to the set and not to any field in it.
 */
export const FIELDSET = Object.freeze({
  group: 'fset',
  legend: 'fset-l',
  description: 'fset-d',
  /** Fields laid out on one line, for two-and-three-character numbers that would otherwise stack. */
  row: 'fset-row',
  summary: 'fset-s',
});

/**
 * The two slots on a field's label row that are not the label.
 *
 * `badge` states something about the field's VALUE that its content cannot — a rung staged at
 * `0` is off, an unparseable box is not a number — and `action` holds a control that acts on
 * that one field, like resetting it to inherit. Both live here rather than being written at the
 * call site, because a call site cannot write a class at all.
 */
export const FIELD_EXTRA = Object.freeze({
  badge: 'fld-badge',
  action: 'fld-action',
});

/**
 * An on/off switch — state that applies the moment it is flipped, as opposed to a checkbox,
 * which states an intention a later submit acts on.
 *
 * Its own names rather than `CHECKBOX`'s, because the two are not the same control to a
 * reader or to a screen reader: one is `role="checkbox"` inside a form, the other is
 * `role="switch"` that commits on change. Sharing class names would make a future change to
 * one silently restyle the other.
 */
export const SWITCH = Object.freeze({
  /** The track. Paint comes from the `--ctl-*` roles, like every other control here. */
  control: 'ctl-switch',
  /** The moving part. Positioned by the track's `[data-checked]` state, never by JS. */
  thumb: 'ctl-switch-thumb',
  row: 'ctl-switch-row',
  label: 'ctl-switch-label',
  hint: 'ctl-switch-hint',
});

/**
 * A value that is already set, shown as a chip. Its own names rather than a button tone or
 * a `CHROME` entry, because a chip is neither of those things: chrome narrows what is on
 * screen and a button performs an action, while a chip reports something already decided
 * and carries one control that undoes it.
 */
export const TAG = Object.freeze({
  /**
   * One chip. Its remove control is styled by `.tag button` — a rule keyed on position, so
   * the button must be the one `RemovableTag` renders. That is exactly why the primitive
   * exists rather than the call site writing the pair.
   */
  chip: 'tag',
  /** The row of them. */
  list: 'tags',
  /** The same row holding a sentence instead, when there is nothing set. */
  empty: 'tags tags-empty',
});

/**
 * Tabular figures for a control that holds a number. Not paint, and not a class a caller
 * writes: it is the `numeric` prop on TextInput.
 */
export const CONTROL_NUMERIC = 'num';

/** Layout containers for the chrome family. Composition, not paint. */
export const CHROME_LAYOUT = Object.freeze({
  /** A row of filter pills. Rendered as a `fieldset` so the group has one accessible name. */
  filters: 'filters',
  /**
   * The same row with its trailing margin dropped, for a group that sits inside a header
   * beside a heading. The 16px is right when the group stands alone above a list and wrong
   * in a bottom-aligned row, where it lifts the pills clear of the heading they belong to.
   */
  filtersFlush: 'filters filters-flush',
  /** A horizontal bar holding a search box and its neighbours. */
  bar: 'hbar',
  /** A row of action buttons. */
  buttonRow: 'btn-row',
  pager: 'pager',
});

/**
 * A tabbed view switch — the request, and the conversation about it, sharing one card.
 *
 * Its own names rather than `CHROME.toggle`, which is the family that NARROWS what is on
 * screen: a filter and a view switch leave the reader on the same material, while a tab
 * swaps the material outright and owns a `tabpanel` that has to be announced with it. They
 * also differ in shape on purpose — chrome is a standalone filter pill, while a tab is a
 * rectangular segment embedded in the card's title bar — and sharing a class would make one
 * of those changes silently restyle the other.
 */
export const TABS = Object.freeze({
  /*
   * `vtab`, not `tab`. The app header's section nav already owns `.tab`, and it is a
   * different control — a set of links to other screens, not a switch between panels on
   * this one. Sharing the name would have made either rule silently restyle the other,
   * decided by stylesheet load order rather than by anybody's intent.
   */
  list: 'vtabs',
  tab: 'vtab',
  /** The count beside a tab's name: "Discussion 4". */
  count: 'vtab-n',
});

/**
 * Editing a value where it is read.
 *
 * The same semantic trigger has two contextual presentations. It stays text-like in a dense
 * field list, while the status rail gives editable operational facts a persistent control edge
 * and pencil. Read-only values remain plain, so the distinction is visible without turning
 * every fact on the page into a form field.
 *
 * Its own names rather than a `BUTTON_TONE`, because its paint depends on the surrounding
 * information architecture rather than an action hierarchy. Tones stay a closed set of five.
 */
export const INLINE = Object.freeze({
  /** The value, as a target. */
  trigger: 'inl',
  /** The decorative pencil. Persistent in the rail, hover/focus elsewhere; never load-bearing. */
  pen: 'inl-pen',
  /** The editor, floated over the rail. */
  popover: 'inl-pop',
  /** The editor, opened in the flow of a narrow list instead. */
  panel: 'inl-panel',
  /** Save and Cancel. */
  actions: 'inl-act',
});

export function buttonClass(tone: ButtonTone, size: ButtonSize): string {
  return [BUTTON_BASE, BUTTON_TONE[tone], BUTTON_SIZE[size]].filter(Boolean).join(' ');
}
